import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import '../../../styles/Platforms.css';

const IMAGES_PER_PAGE = 3;
const Mobileapp = () => {
  const location = useLocation();
  const sectionRefs = useRef({});
  const platformData = {
    title: "Mobile Applications",
    description: "Native and cross-platform mobile solutions",
    icon: "📱",
    color: "#6366f1",
    sections: [
      {
        id: "lookit",
        title: "LookIt",
        description: "Comprehensive mobile application suite for information and education",
        items: ["Information", "HealthTips", "Education", "TechTips"],
        features: [
          "Real-time information updates",
          "Health and wellness tips",
          "Educational content",
          "Technology tutorials"
        ],
        images: [
          "/assets/lookit/1.png",
          "/assets/lookit/2.png",
          "/assets/lookit/3.png",
          "/assets/lookit/4.png",
          "/assets/lookit/5.png",
          "/assets/lookit/7.png"
        ],
        appLink: "https://play.google.com/store/apps/details?id=com.makkal.calendar"
      },
      {
        id: "makkal-sinthani-peravai",
        title: "Makkal Sinthani Peravai",
        description: "Community and information platform for public awareness",
        items: ["Barathi Vizha", "News", "Meetings", "Publications"],
        features: [
          "Event management",
          "News dissemination",
          "Meeting coordination",
          "Publication distribution"
        ],
        images: [
          "/assets/makkal/1.png",
          "/assets/makkal/2.png",
          "/assets/makkal/3.png",
          "/assets/makkal/4.png",
          "/assets/makkal/5.png",
          "/assets/makkal/6.png"
        ],
        appLink: "https://play.google.com/store/apps/details?id=com.sinthanai.peravai"
      },
      {
        id: "firstlook",
        title: "FirstLook",
        description: "Entertainment and education platform",
        items: ["Education", "Entertainment"],
        features: [
          "Educational videos",
          "Entertainment content",
          "Interactive learning",
          "Media streaming"
        ],
        images: [
          "/assets/fl/1.png",
          "/assets/fl/2.png",
          "/assets/fl/3.png",
          "/assets/fl/4.png",
          "/assets/fl/5.png",
          "/assets/fl/6.png"
        ],
        appLink: "https://play.google.com/store/apps/details?id=com.mpeople.memorise"
      }
    ]
  };

  /* -------------------- STATE -------------------- */
  const [activePageIndex, setActivePageIndex] = useState(() => {
    const initial = {};
    platformData.sections.forEach(section => {
      initial[section.id] = 0;
    });
    return initial;
  });

  /* -------------------- HASH SCROLL -------------------- */
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash && sectionRefs.current[hash]) {
      setTimeout(() => {
        sectionRefs.current[hash].scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [location.hash]);

  /* -------------------- HELPERS -------------------- */
  const getTotalPages = (sectionId) => {
    const section = platformData.sections.find(s => s.id === sectionId);
    return Math.ceil(section.images.length / IMAGES_PER_PAGE);
  };

  const nextPage = (sectionId) => {
    setActivePageIndex(prev => ({
      ...prev,
      [sectionId]: (prev[sectionId] + 1) % getTotalPages(sectionId)
    }));
  };

  const prevPage = (sectionId) => {
    setActivePageIndex(prev => ({
      ...prev,
      [sectionId]:
        (prev[sectionId] - 1 + getTotalPages(sectionId)) %
        getTotalPages(sectionId)
    }));
  };

  /* -------------------- RENDER -------------------- */
  return (
    <div className="platform-page">

      {/* HERO */}
      <div className="platform-hero" style={{ "--hero-color": platformData.color }}>
        <div className="platform-hero-content">
          <span className="platform-eyebrow">Platform Solution</span>
          <h1>{platformData.title}</h1>
          <p className="platform-description">{platformData.description}</p>

          <ul className="platform-highlights">
            <li>Scalable & Secure Architecture</li>
            <li>Modern UI & Performance Optimized</li>
            <li>Custom Integrations & APIs</li>
          </ul>

          <a href="/contact" className="btn-primary">Get Started</a>
        </div>

        <img className="bannerimgmob" src="/assets/opbg/bannerimgmob.jpg" alt="" />
      </div>

      {/* SECTIONS */}
      <div className="platform-sections">
        <div className="sections-container">

          {/* SIDEBAR */}
          <aside className="sections-sidebar">
            <h3>Sections</h3>
            <ul className="section-nav">
              {platformData.sections.map(section => (
                <li key={section.id}>
                  <a href={`#${section.id}`} className="section-nav-link">
                   &#8594; {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </aside>

          {/* CONTENT */}
          <div className="sections-content">
            {platformData.sections.map(section => (
              <section
                key={section.id}
                id={section.id}
                ref={el => sectionRefs.current[section.id] = el}
                className="platform-section"
              >
                <h2>{section.title}</h2>
                <p className="section-description">{section.description}</p>

                {/* CAROUSEL */}
                <div className="section-carousel">
                  <h4>App Screenshots</h4>

                  <div className="carousel-wrapper">
                    <button
                      className="carousel-btn prev-btn"
                      onClick={() => prevPage(section.id)}
                    >
                      ‹
                    </button>

                    <div className="carousel-image-containermob three-grid">
                      {section.images
                        .slice(
                          activePageIndex[section.id] * IMAGES_PER_PAGE,
                          activePageIndex[section.id] * IMAGES_PER_PAGE + IMAGES_PER_PAGE
                        )
                        .map((img, index) => (
                          <img
                            key={index}
                            src={img}
                            alt="App Screenshot"
                            className="carousel-image"
                            loading="lazy"
                          />
                        ))}
                    </div>

                    <button
                      className="carousel-btn next-btn"
                      onClick={() => nextPage(section.id)}
                    >
                      ›
                    </button>
                  </div>
                </div>

                {/* DOWNLOAD */}
                <a
                  href={section.appLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="app-link"
                  style={{ backgroundColor: platformData.color }}
                >
                  Download App
                </a>

                {/* FEATURES */}
                <h4>Key Features</h4>
                <ul className="features-list">
                  {section.features.map((f, i) => (
                    <li key={i}>✓ {f}</li>
                  ))}
                </ul>

                {/* CATEGORIES */}
                <h4>Categories</h4>
                <ul className="items-grid">
                  {section.items.map((item, i) => (
                    <li key={i} className="item-card">
                      {item}
                    </li>
                  ))}
                </ul>

              </section>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Mobileapp;
